import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import type { DepotModel } from '../api/client/chargers'
import type { CurrentLocation, LocationOfInterestResponseModel, VehicleModel } from '../api/client/fleet-management'
import type { ChargeFallbackLevel, ChargeModelStatus, ChargerClass, PowerCurveRegionSource } from '~/data/utils/vehicleCatalog'
import type { VehicleEfficiencyBaselineSource, VehicleEfficiencyFallbackLevel, VehicleEfficiencyModelStatus } from '~/data/utils/vehicleEfficiency'
import type { LocationOfInterestType } from '../types/location'
import { type VehicleStatus, vehicleStatuses } from '../types/vehicle'

type PartialVehicle = Pick<VehicleModel, 'vehicleNm' | 'make' | 'model' | 'modelYear' | 'id'>
type VehicleOptions = {
  invalidStr?: string
  useOriginalName?: boolean
}

const defaultOptions = {
  invalidStr: 'Unknown Vehicle',
  useOriginalName: false
}

export function formatVehicleName(vehicle?: PartialVehicle, options?: VehicleOptions) {
  const { invalidStr, useOriginalName } = { ...defaultOptions, ...options }
  return vehicle ? parseVehicleName(vehicle, useOriginalName) : invalidStr
}

/**
 * Formats a vehicle's name for export (to a CSV). Uses an empty string for invalid values.
 */
export const formatVehicleNameExport = (vehicle?: PartialVehicle) => {
  return formatVehicleName(vehicle, { invalidStr: '' })
}

export function isVehicleStatus(status?: string): status is VehicleStatus {
  return status !== undefined && vehicleStatuses.includes(status as VehicleStatus)
}

/**
 * Returns true if the vehicle is considered stale. A vehicle is considered stale if:
 *   1. there's no `telematicsTimestamp`; or
 *   2. the telematics data is older than 72 hours
 */
export function vehicleIsStale(telematicsTimestamp?: string) {
  if (!telematicsTimestamp) return false
  return dayjs().subtract(72, 'hours').isAfter(telematicsTimestamp)
}

type VehicleNameEntity = Pick<VehicleModel, 'vehicleNm' | 'vehicleDisplayNm'>
export function parseVehicleName(vehicle?: VehicleNameEntity, useOriginalName?: boolean) {
  const { vehicleNm, vehicleDisplayNm } = vehicle ?? {}
  if (!vehicleNm) return 'Unknown Vehicle'
  return useOriginalName ? vehicleNm : vehicleDisplayNm || vehicleNm
}

export function useLocationOfInterestTypeFormatter() {
  const t = useTranslations('data.locationOfInterest')
  return useCallback((loiType: LocationOfInterestType) => t(`types_${loiType}`), [t])
}

export function useVehicleStatusFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((status?: string) => t(`status_${isVehicleStatus(status) ? status : 'Unknown'}`), [t])
}

export function useDriverTypeFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((isAutonomous?: boolean) => t(isAutonomous ? 'driverType_autonomous' : 'driverType_human'), [t])
}

/** Efficiency model status (fleet-management `VehicleEfficiencyModel.status`, ENG-13046). */
export function useEfficiencyModelStatusFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((status: VehicleEfficiencyModelStatus) => t(`efficiencyModelStatus_${status}`), [t])
}

/**
 * Which curve an efficiency model is using (`VehicleEfficiencyModel.fallbackLevel`, ENG-13046):
 * the vehicle's own, a cohort level, or the platform prior curve (contract v6 F7).
 */
export function useEfficiencyFallbackLevelFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((level: VehicleEfficiencyFallbackLevel) => t(`efficiencyFallbackLevel_${level}`), [t])
}

/**
 * Where an efficiency model's mild-weather baseline came from (`VehicleEfficiencyModel.baselineSource`,
 * contract v6 F1): the vehicle's own median, a cohort median, the capability set or the configured value.
 */
export function useEfficiencyBaselineSourceFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((source: VehicleEfficiencyBaselineSource) => t(`efficiencyBaselineSource_${source}`), [t])
}

/** Charge model status (fleet-management `VehicleChargeModel.status`, ENG-13046). */
export function useChargeModelStatusFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((status: ChargeModelStatus) => t(`chargeModelStatus_${status}`), [t])
}

/** Which curve a charge model is using while the set has no trained curve (`VehicleChargeModel.fallbackLevel`). */
export function useChargeFallbackLevelFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((level: ChargeFallbackLevel) => t(`chargeFallbackLevel_${level}`), [t])
}

/** The charger class a LEARNED power curve was fitted for (`VehicleCatalogPowerCurve.chargerClass`). */
export function useChargerClassFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((chargerClass: ChargerClass) => t(`chargerClass_${chargerClass}`), [t])
}

/** Where a stretch of a LEARNED power curve comes from (`PowerCurveSourceRegion.source`). */
export function usePowerCurveRegionSourceFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((source: PowerCurveRegionSource) => t(`powerCurveRegionSource_${source}`), [t])
}

export function useVehicleCommissionStatusFormatter() {
  const t = useTranslations('components.VehicleCommissionStatusAutocomplete')
  return useCallback(
    (status?: VehicleModel['vehicleCommissionStatus']) => (status ? t(`labels_${status}`) : undefined),
    [t]
  )
}

type AuthTagStatus = 'ACTIVE' | 'DEACTIVATED'
export function useAuthTagStatusFormatter() {
  const t = useTranslations('data.vehicles')
  return useCallback((status: AuthTagStatus) => t(`authTagStatus_${status}`), [t])
}

type LoiMap = Record<string, LocationOfInterestResponseModel>
type SiteMap = Record<string, DepotModel>

/**
 * Derives location of interest (LOI) names from a vehicle's current location.
 * For each LOI associated with the location, returns the appropriate name based on type:
 * - If the LOI is a Site, returns the site's depot name
 * - Otherwise, returns the LOI's name
 *
 * @param currentLocation - The vehicle's current location object
 * @param orgSites - Map of site IDs to depot/site models (same shape as `useOrgSitesMap`)
 * @param lois - Map of LOI IDs to LocationOfInterestModel objects
 * @returns Array of LOI names, or undefined if no location data is available
 */
export function deriveLoiNames(
  currentLocation: CurrentLocation | undefined,
  orgSites: SiteMap,
  lois: LoiMap
): string[] | undefined {
  return currentLocation?.locationOfInterestModels?.map(({ type, id = '' }) =>
    type === 'Site' ? (orgSites[id]?.depotNm ?? '') : (lois[id]?.loiNm ?? '')
  )
}
